import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiPost } from '@/auth/api-client';
import { useBootstrap } from '@/auth/bootstrap-context';
import { getSubscription } from '@/modules/subscription/subscription-api';
import { isActiveSubscription } from '@/modules/subscription/subscription-view';
// The provider webhook may arrive a few seconds after the redirect, so poll a handful of times.
const MAX_ATTEMPTS = 6;
const INTERVAL_MS = 2500;
/**
 * Landing page after the payment provider redirects back (BILLING_RETURN_URL).
 * Polls the subscription until it becomes active (webhook processed) or gives up gracefully.
 */
export default function BillingReturnPage() {
    const navigate = useNavigate();
    const bootstrap = useBootstrap();
    const [params] = useSearchParams();
    const [phase, setPhase] = useState('checking');
    const attemptsRef = useRef(0);
    // Dev only: the fake gateway never sends a webhook, so the return URL carries `fake_payment`
    // and we self-trigger the (public) webhook. Real YooKassa redirects without this param.
    const fakePaymentId = params.get('fake_payment');
    useEffect(() => {
        let alive = true;
        let timer;
        async function poll() {
            try {
                const sub = await getSubscription();
                if (!alive)
                    return;
                if (isActiveSubscription(sub.status)) {
                    // Перечитываем bootstrap: подписка выдала гранты → cloudSyncStatus/
                    // storagePolicy обновятся, и UI импорта появится без ручного refresh.
                    await bootstrap.refresh().catch(() => { });
                    if (!alive)
                        return;
                    setPhase('active');
                    return;
                }
            }
            catch {
                // ignore — retry below
            }
            if (!alive)
                return;
            attemptsRef.current += 1;
            if (attemptsRef.current >= MAX_ATTEMPTS) {
                setPhase('pending');
                return;
            }
            timer = setTimeout(poll, INTERVAL_MS);
        }
        async function start() {
            if (fakePaymentId) {
                try {
                    await apiPost('/api/v1/billing/yookassa/webhook', {
                        type: 'notification',
                        event: 'payment.succeeded',
                        object: { id: fakePaymentId },
                    });
                }
                catch {
                    // ignore — the poll below will still reflect real state
                }
            }
            poll();
        }
        start();
        return () => {
            alive = false;
            clearTimeout(timer);
        };
        // bootstrap.refresh стабилен (useCallback); не добавляем bootstrap в deps,
        // чтобы обновление bootstrapData не перезапускало опрос.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fakePaymentId]);
    return (_jsxs("div", { style: { maxWidth: 420, margin: '0 auto', padding: '48px 24px', textAlign: 'center' }, children: [phase === 'checking' && (_jsxs(_Fragment, { children: [_jsx("div", { style: { fontSize: 32, marginBottom: 12 }, children: "\u23F3" }), _jsx("h2", { style: { fontSize: 18, margin: '0 0 8px' }, children: "\u041F\u0440\u043E\u0432\u0435\u0440\u044F\u0435\u043C \u043E\u043F\u043B\u0430\u0442\u0443\u2026" }), _jsx("p", { style: { fontSize: 14, color: 'var(--text-2)' }, children: "\u042D\u0442\u043E \u0437\u0430\u0439\u043C\u0451\u0442 \u043D\u0435\u0441\u043A\u043E\u043B\u044C\u043A\u043E \u0441\u0435\u043A\u0443\u043D\u0434." })] })), phase === 'active' && (_jsxs(_Fragment, { children: [_jsx("div", { style: { fontSize: 32, marginBottom: 12 }, children: "\u2705" }), _jsx("h2", { style: { fontSize: 18, margin: '0 0 8px' }, children: "\u0421\u0438\u043D\u0445\u0440\u043E\u043D\u0438\u0437\u0430\u0446\u0438\u044F \u043F\u043E\u0434\u043A\u043B\u044E\u0447\u0435\u043D\u0430" }), _jsx("p", { style: { fontSize: 14, color: 'var(--text-2)', marginBottom: 20 }, children: "\u041F\u043E\u0434\u043F\u0438\u0441\u043A\u0430 \u0430\u043A\u0442\u0438\u0432\u043D\u0430. \u041E\u0431\u043B\u0430\u0447\u043D\u044B\u0435 \u0431\u044E\u0434\u0436\u0435\u0442\u044B \u0441\u043D\u043E\u0432\u0430 \u0441\u0438\u043D\u0445\u0440\u043E\u043D\u0438\u0437\u0438\u0440\u0443\u044E\u0442\u0441\u044F." }), _jsx("button", { className: "btn-primary", onClick: () => navigate('/app'), children: "\u041E\u0442\u043A\u0440\u044B\u0442\u044C \u043F\u0440\u0438\u043B\u043E\u0436\u0435\u043D\u0438\u0435" })] })), phase === 'pending' && (_jsxs(_Fragment, { children: [_jsx("div", { style: { fontSize: 32, marginBottom: 12 }, children: "\uD83D\uDD53" }), _jsx("h2", { style: { fontSize: 18, margin: '0 0 8px' }, children: "\u041E\u043F\u043B\u0430\u0442\u0430 \u043E\u0431\u0440\u0430\u0431\u0430\u0442\u044B\u0432\u0430\u0435\u0442\u0441\u044F" }), _jsx("p", { style: { fontSize: 14, color: 'var(--text-2)', marginBottom: 20 }, children: "\u042D\u0442\u043E \u043C\u043E\u0436\u0435\u0442 \u0437\u0430\u043D\u044F\u0442\u044C \u043D\u0435\u0441\u043A\u043E\u043B\u044C\u043A\u043E \u043C\u0438\u043D\u0443\u0442. \u0421\u0442\u0430\u0442\u0443\u0441 \u043F\u043E\u0434\u043F\u0438\u0441\u043A\u0438 \u043E\u0431\u043D\u043E\u0432\u0438\u0442\u0441\u044F \u0430\u0432\u0442\u043E\u043C\u0430\u0442\u0438\u0447\u0435\u0441\u043A\u0438." }), _jsx("button", { className: "btn-primary", onClick: () => navigate('/app'), children: "\u0412\u0435\u0440\u043D\u0443\u0442\u044C\u0441\u044F \u0432 \u043F\u0440\u0438\u043B\u043E\u0436\u0435\u043D\u0438\u0435" })] }))] }));
}
